import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Clock, MapPin, MessageCircle } from "lucide-react";
import siteSettings from "../data/site-settings.json";
import { CONTACT } from "../constants/testIds";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

const ContactPage = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    product: "",
    message: "",
  });

  const productOptions = [
    "Flour & Baking Products",
    "Rice & Grains",
    "Oils & Ghee",
    "Spices & Seasonings",
    "Nuts & Pulses",
    "Dairy & Grocery Products",
    "General Inquiry",
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const msg = `Hello AL ZAHRAN FLOUR MILL,

My name is ${form.name}.
Phone: ${form.phone}
Product Interest: ${form.product}

Message: ${form.message}

I look forward to hearing from you.`;

    window.open(
      `https://wa.me/${siteSettings.whatsapp}?text=${encodeURIComponent(msg)}`,
      "_blank"
    );
  };

  const contactInfo = [
    {
      icon: <Phone size={20} className="text-premiumGold" />,
      label: "Mobile",
      value: siteSettings.phone,
      href: `tel:${siteSettings.phone}`,
    },
    {
      icon: <Phone size={20} className="text-premiumGold" />,
      label: "Telephone",
      value: siteSettings.landline,
      href: `tel:${siteSettings.landline}`,
    },
    {
      icon: <Mail size={20} className="text-premiumGold" />,
      label: "Email",
      value: siteSettings.email,
      href: `mailto:${siteSettings.email}`,
    },
    {
      icon: <Clock size={20} className="text-premiumGold" />,
      label: "Business Hours",
      value: siteSettings.hours,
      href: null,
    },
    {
      icon: <MapPin size={20} className="text-premiumGold" />,
      label: "Location",
      value: siteSettings.address,
      href: null,
    },
  ];

  return (
    <div className="pt-20" data-testid={CONTACT.pageSection}>
      {/* Hero */}
      <section className="bg-deepCharcoal py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img src={siteSettings.store_image} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-deepCharcoal/60 to-deepCharcoal/85" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.span variants={fadeUp} className="section-label" style={{ color: "#C9A96E" }}>
              Get In Touch
            </motion.span>
            <motion.h1
              variants={fadeUp}
              className="font-cormorant text-5xl sm:text-6xl font-bold text-white mt-3 mb-4"
            >
              Contact Us
            </motion.h1>
            <motion.div variants={fadeUp} className="gold-divider mx-auto mb-5" />
            <motion.p
              variants={fadeUp}
              className="font-outfit text-base text-white/70 max-w-xl mx-auto"
            >
              Reach out to us via WhatsApp, phone, or email. We're available daily from 8:00 AM to midnight.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-shadowWhite">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={stagger}
              viewport={{ once: true }}
            >
              <motion.span variants={fadeUp} className="section-label">Direct Contact</motion.span>
              <motion.h2
                variants={fadeUp}
                className="font-cormorant text-4xl font-bold text-deepCharcoal mt-3 mb-4"
              >
                {siteSettings.company_name}
              </motion.h2>
              <motion.div variants={fadeUp} className="gold-divider mb-8" />

              <motion.div variants={stagger} className="space-y-5 mb-10">
                {contactInfo.map((item, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    className="flex items-start space-x-4 p-4 bg-white border border-goldBorder"
                  >
                    <div className="mt-0.5 flex-shrink-0">{item.icon}</div>
                    <div>
                      <p className="font-outfit text-xs text-premiumGold font-semibold uppercase tracking-wide mb-1">
                        {item.label}
                      </p>
                      {item.href ? (
                        <a
                          href={item.href}
                          className="font-outfit text-base text-deepCharcoal hover:text-premiumGold transition-colors font-medium"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="font-outfit text-base text-deepCharcoal font-medium">
                          {item.value}
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              {/* WhatsApp CTA */}
              <motion.div variants={fadeUp}>
                <a
                  href={`https://wa.me/${siteSettings.whatsapp}?text=${encodeURIComponent(
                    "Hello AL ZAHRAN FLOUR MILL, I would like information about your products."
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-3 bg-[#25D366] text-white px-8 py-4 font-outfit font-semibold text-sm hover:bg-[#1EBE5A] transition-colors"
                >
                  <MessageCircle size={20} />
                  <span>Chat on WhatsApp</span>
                </a>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={stagger}
              viewport={{ once: true }}
            >
              <motion.div variants={fadeUp} className="bg-white border border-goldBorder p-8 lg:p-10">
                <div className="mb-8">
                  <span className="section-label">Send Message</span>
                  <h3 className="font-cormorant text-3xl font-bold text-deepCharcoal mt-2 mb-1">
                    Quick Inquiry
                  </h3>
                  <p className="font-outfit text-sm text-[#4A4A4A]">
                    Fill in the form below — we'll open WhatsApp with your message pre-filled.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block font-outfit text-xs font-semibold text-[#4A4A4A] uppercase tracking-wide mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your name"
                      className="w-full border border-goldBorder bg-shadowWhite px-4 py-3 font-outfit text-sm text-deepCharcoal focus:outline-none focus:border-premiumGold transition-colors"
                      data-testid={CONTACT.formName}
                    />
                  </div>

                  <div>
                    <label className="block font-outfit text-xs font-semibold text-[#4A4A4A] uppercase tracking-wide mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+971 XX XXX XXXX"
                      className="w-full border border-goldBorder bg-shadowWhite px-4 py-3 font-outfit text-sm text-deepCharcoal focus:outline-none focus:border-premiumGold transition-colors"
                      data-testid={CONTACT.formPhone}
                    />
                  </div>

                  <div>
                    <label className="block font-outfit text-xs font-semibold text-[#4A4A4A] uppercase tracking-wide mb-2">
                      Product Interest
                    </label>
                    <select
                      value={form.product}
                      onChange={(e) => setForm({ ...form, product: e.target.value })}
                      className="w-full border border-goldBorder bg-shadowWhite px-4 py-3 font-outfit text-sm text-deepCharcoal focus:outline-none focus:border-premiumGold transition-colors"
                      data-testid={CONTACT.formProduct}
                    >
                      <option value="">Select a product category</option>
                      {productOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block font-outfit text-xs font-semibold text-[#4A4A4A] uppercase tracking-wide mb-2">
                      Message
                    </label>
                    <textarea
                      rows={4}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Tell us about your requirements..."
                      className="w-full border border-goldBorder bg-shadowWhite px-4 py-3 font-outfit text-sm text-deepCharcoal focus:outline-none focus:border-premiumGold transition-colors resize-none"
                      data-testid={CONTACT.formMessage}
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full inline-flex items-center justify-center space-x-2 bg-premiumGold text-deepCharcoal py-4 font-outfit font-semibold text-sm hover:bg-[#B8944A] transition-colors duration-200"
                    data-testid={CONTACT.formSubmit}
                  >
                    <MessageCircle size={16} />
                    <span>Send via WhatsApp</span>
                  </button>

                  <p className="font-outfit text-xs text-[#4A4A4A] text-center">
                    Clicking submit opens WhatsApp with your message. No data is stored on our servers.
                  </p>
                </form>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bottom Info Bar */}
      <section className="bg-softIvory border-t border-goldBorder py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            <div>
              <p className="font-cormorant text-2xl font-bold text-deepCharcoal">{siteSettings.phone}</p>
              <p className="section-label mt-1">Mobile</p>
            </div>
            <div>
              <p className="font-cormorant text-2xl font-bold text-deepCharcoal">{siteSettings.hours}</p>
              <p className="section-label mt-1">Business Hours</p>
            </div>
            <div>
              <p className="font-cormorant text-2xl font-bold text-deepCharcoal">{siteSettings.landline}</p>
              <p className="section-label mt-1">Telephone</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
